import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarPlus, Trash2, CalendarCheck2, BellRing } from "lucide-react";
import { REMINDER_OPTIONS } from "@/components/settings/RealEstateSettingsTab";
import { toast } from "sonner";
import { API, api, apiError } from "@/lib/api";
import { OUTCOME_LABELS } from "@/lib/realestateConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NewVisitDialog = ({ property, open, onOpenChange, onChanged }) => {
  const [form, setForm] = useState({ visitor_name: "", visitor_contact: "", date: new Date().toISOString().slice(0, 10), time: "", notes: "", add_to_calendar: true, reminder_minutes: null });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/properties/${property.id}/visits`, { ...form, time: form.time || null });
      toast.success(form.add_to_calendar ? "Visita creada y añadida al calendario" : "Visita creada");
      onOpenChange(false);
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-visit-dialog">
        <DialogHeader><DialogTitle>Nueva visita</DialogTitle><DialogDescription>Se sincroniza con el calendario del Espacio y puedes exportarla a tu calendario externo.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Nombre del visitante *</Label><Input data-testid="visit-name-input" value={form.visitor_name} onChange={set("visitor_name")} required placeholder="Ana Pérez" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Contacto</Label><Input data-testid="visit-contact-input" value={form.visitor_contact} onChange={set("visitor_contact")} placeholder="Teléfono o email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Fecha *</Label><Input data-testid="visit-date-input" type="date" value={form.date} onChange={set("date")} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Hora</Label><Input data-testid="visit-time-input" type="time" value={form.time} onChange={set("time")} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={set("notes")} placeholder="Viene con pareja, busca 3 habitaciones…" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Aviso previo (Telegram / push)</Label>
            <Select value={form.reminder_minutes === null ? "default" : String(form.reminder_minutes)} onValueChange={(v) => setForm({ ...form, reminder_minutes: v === "default" ? null : Number(v) })}>
              <SelectTrigger className="h-9" data-testid="visit-reminder-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Según ajustes del Espacio</SelectItem>
                {REMINDER_OPTIONS.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {!form.time && <p className="text-[11px] text-muted-foreground">Indica una hora para poder avisarte.</p>}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.add_to_calendar} onCheckedChange={(v) => setForm({ ...form, add_to_calendar: !!v })} data-testid="visit-calendar-checkbox" /> Añadir al calendario del Espacio
          </label>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" disabled={saving} data-testid="visit-create-submit">{saving ? "Guardando…" : "Crear visita"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const VisitsPanel = ({ property, visits, onChanged, canWrite }) => {
  const [open, setOpen] = useState(false);
  const setOutcome = async (v, outcome) => {
    try { await api.put(`/realestate/visits/${v.id}`, { outcome }); onChanged(); } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (v) => {
    if (!window.confirm("¿Eliminar esta visita (y su evento del calendario)?")) return;
    try { await api.delete(`/realestate/visits/${v.id}`); toast.success("Visita eliminada"); onChanged(); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="space-y-2" data-testid="visits-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{visits.length ? `${visits.length} visitas registradas` : "Aún no hay visitas"}</p>
        <div className="flex items-center gap-2">
          <Link to="/calendar" className="text-xs text-primary hover:underline flex items-center gap-1"><CalendarCheck2 size={13} /> Ver calendario</Link>
          {canWrite && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="visit-add-btn"><Plus size={13} className="mr-1" /> Visita</Button>}
        </div>
      </div>
      <div className="space-y-1.5">
        {visits.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs" data-testid={`visit-row-${v.id}`}>
            <div className="flex-1 min-w-[160px]">
              <p className="font-medium">{v.visitor_name} {v.visitor_contact && <span className="text-muted-foreground font-normal">· {v.visitor_contact}</span>}</p>
              <p className="text-muted-foreground">{new Date(v.date + "T00:00:00").toLocaleDateString()} {v.time && `· ${v.time}`} {v.notes && `· ${v.notes}`}</p>
            </div>
            {canWrite ? (
              <Select value={v.outcome || "pending"} onValueChange={(o) => setOutcome(v, o)}>
                <SelectTrigger className="h-7 w-32 text-xs" data-testid={`visit-outcome-${v.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(OUTCOME_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            ) : <span className="text-muted-foreground">{OUTCOME_LABELS[v.outcome] || ""}</span>}
            {v.time && v.reminder_minutes > 0 && (
              <span className={`inline-flex items-center gap-1 text-[10px] ${v.reminded_at ? "text-emerald-600" : "text-muted-foreground"}`} title={v.reminded_at ? "Aviso enviado" : "Aviso programado"} data-testid={`visit-reminder-${v.id}`}>
                <BellRing size={11} /> {REMINDER_OPTIONS.find(([m]) => m === v.reminder_minutes)?.[1] || `${v.reminder_minutes} min antes`}
              </span>
            )}
            <a href={`${API}/realestate/visits/${v.id}/ics`} className="p-1 rounded hover:bg-secondary" title="Añadir a calendario externo (.ics)" data-testid={`visit-ics-${v.id}`}><CalendarPlus size={14} /></a>
            {canWrite && <button onClick={() => remove(v)} className="p-1 rounded hover:bg-destructive/10 text-destructive" title="Eliminar" data-testid={`visit-delete-${v.id}`}><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
      <NewVisitDialog property={property} open={open} onOpenChange={setOpen} onChanged={onChanged} />
    </div>
  );
};
